import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarPlus, MapPin, Check, CheckCircle2, ChevronDown, Send, User, Briefcase, Clock } from 'lucide-react';
import GooglePlacesAddress, { type AddressData } from '../shared/GooglePlacesAddress';
import type { BookingFormOption, RoutyBookingCollectorArtifact } from '../../business/routy/routyArtifacts';
import {
  isValidEmail,
  isValidName,
  isValidNameLength,
  isTenDigitPhone,
  clampPhoneInput,
  MAX_NAME_LENGTH,
} from '../../utils/validation';

export interface CollectedBooking {
  mode: 'new' | 'existing';
  contactId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  lat: number | null;
  lng: number | null;
  contactAddressId?: string;
  bookingFormId: string;
  bookingFormName: string;
  preferredDate: string;
  preferredTimeRange: string;
}

interface BookingCollectorCardProps {
  artifact: RoutyBookingCollectorArtifact;
  disabled?: boolean;
  onSubmit: (booking: CollectedBooking) => void;
}

const TIME_RANGES = [
  { value: '', label: 'Any time' },
  { value: 'Morning', label: 'Morning' },
  { value: 'Afternoon', label: 'Afternoon' },
  { value: 'Evening', label: 'Evening' },
];

function buildFullAddress(data: AddressData): string {
  const cityState = [data.city, data.state].filter(Boolean).join(', ');
  return [data.address1, cityState, data.zipCode].filter(Boolean).join(', ').trim();
}

const inputClass =
  'w-full rounded-lg border border-ink-200 bg-surface px-3 py-2 text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-60';

/**
 * Native-feeling service picker: a listbox popover showing each form's name,
 * duration, and description (which is what tells apart same-named forms like
 * the several "Sales Appointment (45 min)" entries a tenant can have).
 */
const ServiceSelect: React.FC<{
  forms: BookingFormOption[];
  value: string;
  disabled?: boolean;
  onChange: (id: string) => void;
}> = ({ forms, value, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = forms.find((f) => f.id === value);

  // Close on outside click / Escape, standard popover behavior.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${inputClass} flex items-center justify-between gap-2 text-left`}
      >
        {selected ? (
          <span className="min-w-0 flex items-baseline gap-1.5">
            <span className="truncate font-medium text-ink-800">{selected.name}</span>
            {selected.durationMinutes ? (
              <span className="flex-shrink-0 text-xs text-ink-400">{selected.durationMinutes} min</span>
            ) : null}
          </span>
        ) : (
          <span className="text-ink-400">Select a service…</span>
        )}
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-ink-200 bg-surface shadow-lg shadow-ink-950/10 py-1"
        >
          {forms.map((f) => {
            const isSelected = f.id === value;
            return (
              <button
                key={f.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(f.id);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left transition-colors hover:bg-ink-50 ${
                  isSelected ? 'bg-blue-50/60' : ''
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${isSelected ? 'font-semibold text-blue-700' : 'font-medium text-ink-800'}`}>
                    {f.name}
                  </span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    {f.durationMinutes ? <span className="text-xs text-ink-400">{f.durationMinutes} min</span> : null}
                    {isSelected && <Check className="w-3.5 h-3.5 text-blue-600" />}
                  </span>
                </span>
                {f.description && (
                  <span className="mt-0.5 block text-xs text-ink-500 leading-snug line-clamp-2">{f.description}</span>
                )}
              </button>
            );
          })}
          {forms.length === 0 && (
            <p className="px-3 py-2 text-xs text-ink-400">No bookable services available.</p>
          )}
        </div>
      )}
    </div>
  );
};

const BookingCollectorCard: React.FC<BookingCollectorCardProps> = ({ artifact, disabled, onSubmit }) => {
  const prefill = artifact.prefill;
  const [firstName, setFirstName] = useState(prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(prefill?.lastName ?? '');
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  const [addressInput, setAddressInput] = useState(prefill?.address ?? '');
  const [fullAddress, setFullAddress] = useState(prefill?.address ?? '');
  const [lat, setLat] = useState<number | null>(prefill?.lat ?? null);
  const [lng, setLng] = useState<number | null>(prefill?.lng ?? null);
  const [bookingFormId, setBookingFormId] = useState(artifact.bookingFormId ?? '');
  // Date/time the user already named in chat arrive pre-selected (they can
  // still change them) so nothing has to be typed twice.
  const [preferredDate, setPreferredDate] = useState(artifact.preferredDate ?? '');
  const [preferredTimeRange, setPreferredTimeRange] = useState(
    artifact.preferredTimeRange && TIME_RANGES.some((t) => t.value === artifact.preferredTimeRange)
      ? artifact.preferredTimeRange
      : '',
  );
  const [submitted, setSubmitted] = useState(false);

  const isExisting = artifact.mode === 'existing';
  const locked = disabled || submitted;

  const todayKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  const handleAddressSelect = (data: AddressData) => {
    const full = buildFullAddress(data);
    setAddressInput(full || addressInput);
    setFullAddress(full);
    setLat(data.coordinates?.lat ?? null);
    setLng(data.coordinates?.lng ?? null);
  };

  const handleAddressInput = (value: string) => {
    if (value === addressInput) return;
    setAddressInput(value);
    setFullAddress('');
    setLat(null);
    setLng(null);
  };

  const selectedForm = artifact.forms.find((f) => f.id === bookingFormId);
  const addressReady = addressInput.trim().length > 0;

  // Validate new-customer details with the SAME rules as the New Booking modal
  // (utils/validation) so a malformed email/phone can't slip into the created
  // contact and silently vanish from the appointment (QA 86bauqbed: "Jenner @"
  // as a last name, an email with no TLD, a 17-digit phone all got through).
  const contactErrors: { firstName?: string; lastName?: string; email?: string; phone?: string } = {};
  if (!isExisting) {
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    const ph = phone.trim();
    if (fn && !isValidNameLength(fn)) contactErrors.firstName = `First name must be ${MAX_NAME_LENGTH} characters or less`;
    else if (fn && !isValidName(fn)) contactErrors.firstName = 'Letters, spaces, hyphens and apostrophes only';
    if (ln && !isValidNameLength(ln)) contactErrors.lastName = `Last name must be ${MAX_NAME_LENGTH} characters or less`;
    else if (ln && !isValidName(ln)) contactErrors.lastName = 'Letters, spaces, hyphens and apostrophes only';
    if (em && !isValidEmail(em)) contactErrors.email = 'Enter a valid email address';
    if (ph && !isTenDigitPhone(ph)) contactErrors.phone = 'Enter a 10-digit phone number';
  }
  const hasContactErrors = Object.keys(contactErrors).length > 0;
  const contactReady =
    isExisting ||
    (!!firstName.trim() && !!lastName.trim() && !!email.trim() && !!phone.trim() && !hasContactErrors);
  const canSubmit = !!bookingFormId && !!preferredDate && addressReady && !!contactReady;

  const fieldClass = (error?: string) =>
    error ? `${inputClass} border-red-300 focus:border-red-400 focus:ring-red-500/20` : inputClass;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitted(true);
    onSubmit({
      mode: artifact.mode,
      contactId: prefill?.contactId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      address: fullAddress || addressInput.trim(),
      lat,
      lng,
      contactAddressId: prefill?.contactAddressId,
      bookingFormId,
      bookingFormName: selectedForm?.name || 'Service',
      preferredDate,
      preferredTimeRange,
    });
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <CalendarPlus className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">
          {isExisting ? 'Book an appointment' : 'New booking'}
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Contact */}
        {isExisting ? (
          <div className="rounded-lg border border-ink-200 bg-ink-50/40 px-3 py-2 flex items-center gap-2">
            <User className="w-4 h-4 text-ink-400 flex-shrink-0" />
            <span className="text-sm font-medium text-ink-800 truncate">
              {[firstName, lastName].filter(Boolean).join(' ') || 'Selected contact'}
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-400">
              <User className="w-3 h-3" /> Customer
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={locked}
                  placeholder="First name"
                  className={fieldClass(contactErrors.firstName)}
                />
                {contactErrors.firstName && <p className="mt-1 text-2xs text-red-600">{contactErrors.firstName}</p>}
              </div>
              <div>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={locked}
                  placeholder="Last name"
                  className={fieldClass(contactErrors.lastName)}
                />
                {contactErrors.lastName && <p className="mt-1 text-2xs text-red-600">{contactErrors.lastName}</p>}
              </div>
            </div>
            <div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={locked}
                type="email"
                placeholder="Email"
                className={fieldClass(contactErrors.email)}
              />
              {contactErrors.email && <p className="mt-1 text-2xs text-red-600">{contactErrors.email}</p>}
            </div>
            <div>
              <input
                value={phone}
                onChange={(e) => setPhone(clampPhoneInput(e.target.value))}
                disabled={locked}
                placeholder="Phone"
                className={fieldClass(contactErrors.phone)}
              />
              {contactErrors.phone && <p className="mt-1 text-2xs text-red-600">{contactErrors.phone}</p>}
            </div>
          </div>
        )}

        {/* Address */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-400">
            <MapPin className="w-3 h-3" /> Service address
          </div>
          <div className="relative">
            <GooglePlacesAddress
              value={addressInput}
              onChange={handleAddressSelect}
              onInputChange={handleAddressInput}
              placeholder="Search address..."
              searchType="all"
              enableValidation={false}
              disabled={locked}
              className={`${inputClass} pl-8`}
            />
            <MapPin
              className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                lat != null && lng != null ? 'text-emerald-500' : 'text-ink-400'
              }`}
            />
          </div>
        </div>

        {/* Service */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-400">
            <Briefcase className="w-3 h-3" /> Service
          </div>
          <ServiceSelect
            forms={artifact.forms}
            value={bookingFormId}
            disabled={locked}
            onChange={setBookingFormId}
          />
        </div>

        {/* Preferences */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-400">
              <CalendarPlus className="w-3 h-3" /> Preferred date
            </div>
            <input
              value={preferredDate}
              onChange={(e) => setPreferredDate(e.target.value)}
              disabled={locked}
              type="date"
              min={todayKey}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-400">
              <Clock className="w-3 h-3" /> Time
            </div>
            <select
              value={preferredTimeRange}
              onChange={(e) => setPreferredTimeRange(e.target.value)}
              disabled={locked}
              className={inputClass}
            >
              {TIME_RANGES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="px-3 pb-3">
        {submitted ? (
          <div className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-600">
            <CheckCircle2 className="w-4 h-4" />
            Sent to Routy
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={locked || !canSubmit}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {canSubmit ? 'Find available times' : 'Fill in the details to continue'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingCollectorCard;
